import parser from 'any-date-parser';

import {
  Button,
  Divider,
  Grid,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { closeModal } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { handleSubmitError } from '@utils/forms';

import {
  CreateMedicalDoctorRequestBody,
  MedicalDoctor,
  UpdateMedicalDoctorRequest,
} from '@interfaces/medicalDoctors.interface';

import {
  useCreateMedicalDoctorMutation,
  useUpdateMedicalDoctorMutation,
} from '@api/medicalDoctors.api';

import useFormEnhanced from '@hooks/useFormEnhanced';
import useUserPermissionGuard from '@hooks/useUserPermissionGuard';

import CitySelector from '../common/locations/CitySelector';
import NationSelector from '../common/locations/NationSelector';
import ProvinceSelector from '../common/locations/ProvinceSelector';
import ZipSelector from '../common/locations/ZipSelector';
import MedicalOrganizationsSelector from '../medicalOrganizations/MedicalOrganizationsSelector';
import MedicalSpecialitySelector from '../medicalSpecialities/MedicalSpecialitySelector';

export const UPSERT_MEDICAL_DOCTOR_FORM_MODAL_ID = 'upsertMedicalDoctor';

interface UpsertMedicalDoctorFormProps {
  medicalDoctor?: MedicalDoctor;
  afterSubmit?: (newMedicalDoctor: MedicalDoctor) => void;
}

export default function UpsertMedicalDoctorForm({
  medicalDoctor,
  afterSubmit,
}: UpsertMedicalDoctorFormProps) {
  const canReadAllMedicalOrganizations = useUserPermissionGuard(
    'MEDICAL_ORGANIZATIONS.READ',
    false,
  );

  const initialValues = {
    name: medicalDoctor?.user.name || '',
    email: medicalDoctor?.user.email || '',
    title: medicalDoctor?.title || '',
    birthDate: medicalDoctor?.birthDate
      ? new Date(medicalDoctor.birthDate)
      : undefined,
    streetAddress1: medicalDoctor?.streetAddress1 || '',
    streetAddress2: medicalDoctor?.streetAddress2 || '',
    postalCode: medicalDoctor?.postalCode || '',
    locality: medicalDoctor?.locality || '',
    stateProvince: medicalDoctor?.stateProvince || '',
    countryCode: medicalDoctor?.countryCode || '',
    socialSecurityNumber: medicalDoctor?.socialSecurityNumber || '',
    mobilePhone: medicalDoctor?.mobilePhone || '',
    homePhone: medicalDoctor?.homePhone || '',
    specialityId: medicalDoctor?.speciality.id || '',
    medicalOrganizationsIds:
      medicalDoctor?.medicalOrganizations.map((org) => org.id) || [],
  };

  const form = useFormEnhanced({
    initialValues,
    normalizeFields: {
      capitalizeFields: [
        'name',
        'locality',
        'streetAddress1',
        'streetAddress2',
      ],
      uppercaseFields: ['socialSecurityNumber'],
      lowercaseFields: ['email'],
    },
  });

  const [createMedicalDoctor, { isLoading: isLoadingCreate }] =
    useCreateMedicalDoctorMutation();
  const [updateMedicalDoctor, { isLoading: isLoadingUpdate }] =
    useUpdateMedicalDoctorMutation();

  const onCancelClick = () => {
    closeModal(UPSERT_MEDICAL_DOCTOR_FORM_MODAL_ID);
  };

  const onSubmit = async (values: typeof initialValues) => {
    try {
      let newMedicalDoctor;

      const mappedValues = {
        name: values.name,
        email: values.email,
        title: values.title,
        birthDate: values.birthDate
          ? new Date(values.birthDate).toISOString()
          : undefined,
        streetAddress1: values.streetAddress1,
        streetAddress2:
          values.streetAddress2.trim() !== '' ? values.streetAddress2 : null,

        postalCode:
          values.postalCode.trim() !== ''
            ? values.countryCode === 'IT'
              ? values.postalCode
              : null
            : null,
        locality: values.locality,
        stateProvince:
          values.stateProvince.trim() !== ''
            ? values.countryCode === 'IT'
              ? values.stateProvince
              : null
            : null,
        countryCode: values.countryCode,
        socialSecurityNumber:
          values.socialSecurityNumber.trim() !== ''
            ? values.socialSecurityNumber
            : null,
        mobilePhone:
          values.mobilePhone.trim() !== '' ? values.mobilePhone : null,
        homePhone: values.homePhone.trim() !== '' ? values.homePhone : null,
        specialityId: values.specialityId,
        medicalOrganizationsIds: values.medicalOrganizationsIds,
      };

      if (medicalDoctor) {
        const data: UpdateMedicalDoctorRequest = {
          id: medicalDoctor.id,
          body: {
            ...mappedValues,
            medicalOrganizationsIds: canReadAllMedicalOrganizations
              ? mappedValues.medicalOrganizationsIds
              : undefined,
          },
        };

        newMedicalDoctor = await updateMedicalDoctor(data).unwrap();

        notifications.show({
          title: 'Medico modificato',
          message: 'Medico modificato con successo.',
        });
      } else {
        const data: CreateMedicalDoctorRequestBody = {
          ...mappedValues,
          birthDate: mappedValues.birthDate!,
        };

        newMedicalDoctor = await createMedicalDoctor(data).unwrap();

        notifications.show({
          title: 'Medico creato',
          message:
            "Nuovo medico creato con successo. L'utente riceverà un'email con le istruzioni per accedere.",
        });
      }

      closeModal(UPSERT_MEDICAL_DOCTOR_FORM_MODAL_ID);

      afterSubmit?.(newMedicalDoctor);
    } catch (e) {
      handleSubmitError(e, form);
    }
  };

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Grid gutter="xl">
        <Grid.Col span={{ md: 6 }}>
          <Stack gap="sm">
            <Divider label="Dati personali" labelPosition="left" />
            <TextInput
              data-autofocus
              required
              label="Nome"
              {...form.getInputProps('name')}
            />
            <TextInput
              required
              type="email"
              label="Email"
              description={
                medicalDoctor
                  ? null
                  : "Verrà inviata un'email con le istruzioni per accedere"
              }
              {...form.getInputProps('email')}
            />
            <TextInput
              label="Codice fiscale"
              {...form.getInputProps('socialSecurityNumber')}
            />
            <DateInput
              required
              label="Data di nascita"
              valueFormat="DD/MM/YYYY"
              dateParser={(value) => parser.fromString(value, 'it')}
              {...form.getInputProps('birthDate')}
            />
            <Select
              required
              label="Titolo"
              placeholder="Seleziona titolo"
              data={[
                {
                  label: 'Medical Doctor (MD)',
                  value: 'Medical Doctor (MD)',
                },
                {
                  label: 'Dottorato di ricerca (PhD)',
                  value: 'Dottorato di ricerca (PhD)',
                },
                {
                  label: 'Professore associato',
                  value: 'Professore associato',
                },
                {
                  label: 'Professore Ordinario',
                  value: 'Professore Ordinario',
                },
              ]}
              {...form.getInputProps('title')}
            />
            <TextInput
              label="Telefono cellulare"
              {...form.getInputProps('mobilePhone')}
            />
            <TextInput
              label="Telefono fisso"
              {...form.getInputProps('homePhone')}
            />
            <MedicalSpecialitySelector
              required
              label="Specialità"
              {...form.getInputProps('specialityId')}
            />
            {canReadAllMedicalOrganizations && (
              <MedicalOrganizationsSelector
                required
                multiple
                label="Organizzazioni mediche"
                {...form.getInputProps('medicalOrganizationsIds')}
              />
            )}
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ md: 6 }}>
          <Stack gap="sm">
            <Divider label="Indirizzo" labelPosition="left" />
            <NationSelector required {...form.getInputProps('countryCode')} />
            {form.values.countryCode === 'IT' ? (
              <>
                <ProvinceSelector
                  required
                  {...form.getInputProps('stateProvince')}
                />
                <CitySelector
                  required
                  province={form.values.stateProvince}
                  {...form.getInputProps('locality')}
                  disabled={form.values.stateProvince === ''}
                />
                <ZipSelector
                  required
                  city={form.values.locality}
                  {...form.getInputProps('postalCode')}
                  disabled={form.values.locality === ''}
                />
              </>
            ) : (
              <>
                <TextInput
                  required
                  label="Città"
                  {...form.getInputProps('locality')}
                />
              </>
            )}
            <TextInput
              required
              label="Indirizzo"
              {...form.getInputProps('streetAddress1')}
            />
            <TextInput
              label="Indirizzo (2)"
              {...form.getInputProps('streetAddress2')}
            />
          </Stack>
        </Grid.Col>
      </Grid>

      {form.errors.general && (
        <Text c="red" size="xs" ta="right">
          {form.errors.general}
        </Text>
      )}

      <Group justify="right" mt="xl">
        <Button
          onClick={onCancelClick}
          variant="default"
          loading={isLoadingCreate || isLoadingUpdate}
        >
          Annulla
        </Button>
        <Button type="submit" loading={isLoadingCreate || isLoadingUpdate}>
          Conferma
        </Button>
      </Group>
    </form>
  );
}
